import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomer } from "_contexts/useCustomer";
import { usePortalSettings } from "_contexts/usePortalSettings";
import { useApolloClient } from "@apollo/client";
import dayjs from "dayjs";
import { IconAlertTriangle } from "@tabler/icons-react";
import CreditScorePanel from "./_components/CreditScorePanel/CreditScorePanel";
import CreditUsage from "./_components/CreditUsage/CreditUsage";
import PaymentSchedulePanel from "./_components/PaymentSchedulePanel/PaymentSchedulePanel";
import ProgramProgress from "./_components/ProgramProgress/ProgramProgress";
import CreditReports from "./_components/CreditReports/CreditReports";
import UpdatePaymentModal from "../_components/UpdatePaymentModal/UpdatePaymentModal";
import {
  portalCreditReport,
  portalPayments,
  portalTradelines,
} from "_graphql/queries/portal";
import "./Dashboard.scss";

function Dashboard() {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { applicationId: urlAppId } = useParams();
  const { customer, loading, applicationId } = useCustomer();
  const { settings: portalSettings } = usePortalSettings();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [coApplicantReport, setCoApplicantReport] = useState(null);
  const [creditData, setCreditData] = useState({});
  const [payments, setPayments] = useState([]);
  const [tradelines, setTradelines] = useState([]);
  const [tradelineStats, setTradelineStats] = useState({
    balance: 0,
    limit: 0,
  });
  const [updatePaymentModal, setUpdatePaymentModal] = useState(false);

  const firstName = customer?.first_name || "there";

  const failedPayment = payments.find((p) => p.status === "failed");

  // Get contact info from settings
  const contactPhone = portalSettings?.phone_number;
  const contactEmail = portalSettings?.email_address;

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getPaymentStatus = () => {
    const today = dayjs().startOf("day");

    // Get all non-paid payments
    const unpaidPayments = payments.filter(
      (p) => p.status !== "paid" && p.status !== "success"
    );

    if (unpaidPayments.length === 0) return null;

    // Count overdue payments
    const overduePayments = unpaidPayments.filter((p) => {
      const dueDate = dayjs(parseInt(p.next_attempt));
      return dueDate.diff(today, "day") < 0;
    });

    const overdueCount = overduePayments.length;

    // If there are overdue payments
    if (overdueCount > 0) {
      return {
        type: "overdue",
        count: overdueCount,
        message:
          overdueCount === 1
            ? "1 overdue payment"
            : `${overdueCount} overdue payments`,
      };
    }

    // Find the next upcoming payment
    const nextPayment = unpaidPayments.find((p) => {
      const dueDate = dayjs(parseInt(p.next_attempt));
      return dueDate.diff(today, "day") >= 0;
    });

    if (!nextPayment) return null;

    const dueDate = dayjs(parseInt(nextPayment.next_attempt));
    const daysUntil = dueDate.diff(today, "day");

    if (daysUntil === 0) {
      return {
        type: "due-today",
        message: "due today",
      };
    } else if (daysUntil <= 30) {
      return {
        type: "upcoming",
        message: `due in ${daysUntil} day${daysUntil > 1 ? "s" : ""}`,
      };
    } else {
      const months = Math.floor(daysUntil / 30);
      return {
        type: "upcoming",
        message: `due in ${months} month${months > 1 ? "s" : ""}`,
      };
    }
  };

  const paymentStatus = getPaymentStatus();

  const fetchPayments = async () => {
    if (!applicationId) return;

    try {
      const queryData = await client.query({
        variables: { id: applicationId },
        query: portalPayments,
      });

      setPayments(queryData?.data?.portalPayments?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTradelines = async () => {
    if (!applicationId) return;

    try {
      const queryData = await client.query({
        variables: { id: applicationId },
        query: portalTradelines,
      });

      const responseData = queryData?.data?.portalTradelines;
      setTradelines(responseData?.tradelines || []);
      setTradelineStats({
        balance: responseData?.balance || 0,
        limit: responseData?.limit || 0,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReport = async () => {
    if (!applicationId) return;

    try {
      const reportData = await client.query({
        variables: { application_id: applicationId },
        query: portalCreditReport,
      });

      const responseData = reportData?.data?.portalCreditReport || null;
      const creditReportData = responseData?.applicant_report?.report;
      const coApplicantReportData = responseData?.co_applicant_report?.report;

      // Store credit data for program progress
      setCreditData({
        payment: responseData?.payment || 0,
        balance: responseData?.balance || 0,
        limit: responseData?.limit || 0,
      });

      if (creditReportData) {
        try {
          let tempReport = JSON.parse(creditReportData);
          tempReport.issuer = responseData?.applicant_report?.issuer;
          tempReport.createdAt = responseData?.applicant_report?.createdAt;
          tempReport.pdfUrl = responseData?.applicant_report?.pdf_url;
          setReport(tempReport);
        } catch (err) {
          console.log(err);
        }
      }

      if (coApplicantReportData) {
        try {
          let coTempReport = JSON.parse(coApplicantReportData);
          coTempReport.issuer = responseData?.co_applicant_report?.issuer;
          coTempReport.createdAt = responseData?.co_applicant_report?.createdAt;
          coTempReport.pdfUrl = responseData?.co_applicant_report?.pdf_url;
          setCoApplicantReport(coTempReport);
        } catch (err) {
          console.log(err);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchReport();
    fetchPayments();
    fetchTradelines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Greeting */}
      <div className="portal-greeting">
        <h1 className="greeting-text">
          {getGreeting()}, {firstName}
        </h1>
      </div>

      {/* Failed Payment Banner */}
      {failedPayment && (
        <div className="failed-payment-banner">
          <div className="failed-payment-banner__content">
            <IconAlertTriangle size={18} className="failed-payment-banner__icon" />
            <div className="failed-payment-banner__text">
              <strong>Update payment details.</strong>{" "}
              There was a <strong>failed payment</strong>. Please update your
              payment details to ensure there is no disruption in your account.
            </div>
          </div>
          <button
            className="failed-payment-banner__button"
            onClick={() => setUpdatePaymentModal(true)}
          >
            Update Payment Details
          </button>
        </div>
      )}

      {/* Notification Banner */}
      {paymentStatus && (
        <div className="notification-banner">
          {paymentStatus.type === "overdue" ? (
            <>
              <span>You have </span>
              <span className="highlight">{paymentStatus.message}</span>
            </>
          ) : (
            <>
              <span>Your payment is </span>
              <span className="highlight">{paymentStatus.message}</span>
            </>
          )}
          <span>. Questions or concerns? </span>
          {contactPhone || contactEmail ? (
            <>
              {contactPhone && (
                <a
                  href={`tel:+1${contactPhone.replace(/\D/g, "")}`}
                  className="highlight clickable"
                >
                  Call us
                </a>
              )}
              {contactPhone && contactEmail && <span> or </span>}
              {contactEmail && (
                <a
                  href={`mailto:${contactEmail}`}
                  className="highlight clickable"
                >
                  email us
                </a>
              )}
              <span>.</span>
            </>
          ) : (
            <>
              <span>You can reach out using our </span>
              <span className="highlight">contact support</span>
              <span>.</span>
            </>
          )}
        </div>
      )}

      {/* Mobile Program Progress & Credit Score - shown at top on mobile only */}
      <div className="mobile-top-section">
        <ProgramProgress
          customer={customer}
          payments={payments}
          creditData={creditData}
        />
        <CreditScorePanel
          onOpenDrawer={() => setDrawerOpen(true)}
          report={report}
        />
        <div className="credit-usage-panel">
          <CreditUsage
            balance={tradelineStats.balance}
            limit={tradelineStats.limit}
            accounts={tradelines.filter((t) => t.selected)}
            compact
            onSeeAllClick={() => navigate(`/${urlAppId}/accounts`)}
          />
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Left Column - Program Progress & Payment Schedule */}
        <div className="dashboard-column left-column">
          <div className="desktop-only">
            <ProgramProgress
              customer={customer}
              payments={payments}
              creditData={creditData}
            />
          </div>
          <PaymentSchedulePanel
            applicationId={applicationId}
            bankAccount={customer?.bank}
          />
        </div>

        {/* Right Column - Credit Score & Credit Usage (desktop only) */}
        <div className="dashboard-column right-column desktop-only">
          <CreditScorePanel
            onOpenDrawer={() => setDrawerOpen(true)}
            report={report}
          />
          <div className="credit-usage-panel">
            <CreditUsage
              balance={tradelineStats.balance}
              limit={tradelineStats.limit}
              accounts={tradelines.filter((t) => t.selected)}
              compact
              onSeeAllClick={() => navigate(`/${urlAppId}/accounts`)}
            />
          </div>
        </div>
      </div>

      {/* Credit Report Modal */}
      <CreditReports
        closeReport={() => setDrawerOpen(false)}
        open={drawerOpen}
        report={report}
        coApplicantReport={coApplicantReport}
      />

      {/* Update Payment Modal */}
      <UpdatePaymentModal
        open={updatePaymentModal}
        onClose={() => setUpdatePaymentModal(false)}
        onSuccess={() => {
          setUpdatePaymentModal(false);
          fetchPayments();
        }}
        payment={failedPayment}
      />
    </div>
  );
}

export default Dashboard;
